// Sheet/side panel para asignar productos a un slot específico en el
// modo varios-modelos. Reutiliza los 3 paneles existentes (Choose / Pick
// / Upload) variant='mobile' para mantener exactamente la misma UI y
// submenús del flujo principal, sin duplicar código.
//
// Mobile = bottom sheet; desktop = side panel a la derecha. El shell
// solo provee el container responsivo + animaciones — los paneles
// internos rendén sus propios headers iOS, footers de Confirmar, etc.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useExitTransition } from '../../../hooks/useExitTransition';
import { uploadNewProduct } from '../productUploadApi';
import type { MockProductV2 } from '../mockData';
import { ChooseProductPanel } from './ChooseProductPanel';
import { PickProductPanel } from './PickProductPanel';
import { UploadProductPanel } from './UploadProductPanel';
import { AgregarVariosModal } from '../../../components/AgregarVariosModal';

export interface MultiSlotProductPickerSheetProps {
  open: boolean;
  onClose: () => void;
  /** Productos ya seleccionados para el slot — se usan como base si el
   *  user vuelve a abrir el sheet (no aplicado hoy: el state se persiste
   *  en `currentSelection` externa, pero los paneles internos arrancan
   *  vacíos). */
  currentSelection: MockProductV2[];
  /** El user confirmó la selección — los productos van al slot. */
  onConfirm: (products: MockProductV2[]) => void;
  /** id_company para el upload de productos nuevos. */
  userCompanyId: string | null;
  /** Marca activa — se asocia al producto creado, sino no aparece en
   *  "Seleccionar productos" al filtrar por brand. */
  brandId: string | null;
  /** Rubro para activar la sección "Sugeridos" en PickProductPanel. */
  rubro: string | null;
  /** Plan del usuario — el popup "Subir varios productos" lo usa para el tope
   *  de productos por tanda (5 gratis / 20 pago). ADR-684. */
  userPlan: string | null;
  /** Tipos sugeridos del preset elegido. */
  tiposProductoSugeridos?: string[] | null;
  /** Refresca el contexto global de userProducts tras subir uno nuevo. */
  onProductsRefreshed: () => void;
  /** Título del header (view='choose'). Default 'Productos del modelo'. */
  title?: string;
  /** Si true, el sheet abre DIRECTO en el grid de productos cargados
   *  (sin la pantalla Choose de subir / ver). "Volver" cierra el sheet.
   *  Usado para combinar productos en catálogo. */
  directToPick?: boolean;
  /** Selección inicial del grid (en orden — el primero es el principal). */
  initialSelection?: MockProductV2[];
  /** Marca el primer seleccionado como "Principal" en el grid. */
  showPrimary?: boolean;
  /** Descripción / explicación de qué se está seleccionando (subtítulo). */
  pickDescription?: string;
}

export const MultiSlotProductPickerSheet: React.FC<
  MultiSlotProductPickerSheetProps
> = ({
  open,
  onClose,
  onConfirm,
  userCompanyId,
  brandId,
  rubro,
  userPlan,
  tiposProductoSugeridos,
  onProductsRefreshed,
  title = 'Productos del modelo',
  directToPick = false,
  initialSelection,
  showPrimary = false,
  pickDescription,
}) => {
  type PPView = 'choose' | 'pick' | 'upload';
  const [view, setView] = useState<PPView>(directToPick ? 'pick' : 'choose');
  // `useExitTransition` mantiene el sheet montado durante la animación
  // de salida cuando `open` pasa a false — así desliza inverso antes de
  // desmontarse.
  const { mounted, animated } = useExitTransition(open, 320);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingName, setPendingName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Popup "Subir varios productos" — el MISMO de Mi Marca (ADR-684). Se abre
  // solo desde la vista 'upload' (la pantalla "Nuevo producto"), no desde la
  // de elegir producto (ADR-688). Este sheet monta su propia instancia porque
  // vive en un portal aparte del de InterfazV2 y hay que dejarlo por encima de
  // su propio z-211.
  const [bulkOpen, setBulkOpen] = useState(false);
  // Desktop vs mobile para elegir la variante del PickProductPanel.
  const [isDesktop, setIsDesktop] = useState<boolean>(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  // En desktop usamos la variante DESKTOP del panel para la grilla de
  // productos (header con título + grilla + botón Confirmar abajo + X para
  // cerrar), en vez del look mobile. Aplica tanto al flujo directo
  // (combinar / editar outfit del catálogo) como al de slots de modelo.
  const useDesktopPick = isDesktop;

  useEffect(() => {
    const base: PPView = directToPick ? 'pick' : 'choose';
    if (open) {
      setView(base);
      return;
    }
    // Reset interno con delay para no ver el wipe durante la salida.
    const t = setTimeout(() => {
      setView(base);
      setPendingFiles([]);
      setPendingName('');
      setUploadError(null);
    }, 320);
    return () => clearTimeout(t);
  }, [open, directToPick]);

  if (!mounted) return null;

  // "Subir nuevo producto" → entra a la vista 'upload' SIN archivos: el
  // UploadProductPanel muestra la pantalla explicativa (ejemplos + tips) y
  // desde ahí se elige la foto. Mismo patrón que el flujo principal.
  const handleStartNewProduct = () => {
    setPendingFiles([]);
    setPendingName('');
    setUploadError(null);
    setView('upload');
  };

  // Terminó el alta en bloque (el popup se cierra solo): refrescamos el
  // catálogo global y caemos en el grid, donde los recién creados aparecen
  // primeros y sin tildar. Acá el grid es siempre la vista 'pick' —este sheet
  // no tiene el panel fusionado de desktop.
  const handleBulkSaved = () => {
    onProductsRefreshed();
    setView('pick');
  };

  const handleAddMoreFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setPendingFiles((prev) => [...prev, ...arr]);
  };

  const handleRemoveFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCancelUpload = () => {
    setPendingFiles([]);
    setPendingName('');
    setUploadError(null);
    setView('choose');
  };

  const handleConfirmUpload = async () => {
    if (uploading || pendingFiles.length === 0) return;
    if (!userCompanyId) {
      setUploadError('No se encontró la empresa del usuario.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const { variantId, finalName } = await uploadNewProduct({
        companyId: userCompanyId,
        userId: user.id,
        name: pendingName,
        files: pendingFiles,
        brandId,
      });
      onProductsRefreshed();
      // Stub del producto recién creado para confirmar al slot. El
      // padre, al recibir el refresh, va a tener el row real con URL.
      const created: MockProductV2 = {
        id: variantId,
        name: finalName,
        imageUrl: '',
      };
      onConfirm([created]);
      setPendingFiles([]);
      setPendingName('');
    } catch (err) {
      console.error('[MultiSlotProductPickerSheet] upload error', err);
      setUploadError(
        err instanceof Error
          ? err.message
          : 'No pudimos subir el producto. Probá de nuevo.',
      );
    } finally {
      setUploading(false);
    }
  };

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[630px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        {/* Header propio para view='choose' (los otros 2 paneles
            renderizan su propio header iOS / volver). */}
        {view === 'choose' && (
          <>
            <div className="md:hidden py-2 shrink-0">
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
            </div>
            <div className="md:hidden flex items-center justify-between px-4 pb-3 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
              >
                Cancelar
              </button>
              <h3 className="text-[15px] font-semibold text-brand-black/90 truncate max-w-[55%] text-center">
                {title}
              </h3>
              <div className="w-20 flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="hidden md:flex items-center justify-between px-5 py-4 shrink-0">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                {title}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="hidden md:block mx-5 h-px bg-black/[0.08] shrink-0" />
          </>
        )}

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {view === 'choose' && (
            <div className="px-5 pt-2 pb-6 md:pt-4 overflow-y-auto">
              <ChooseProductPanel
                onShowProducts={() => setView('pick')}
                onStartNewProduct={handleStartNewProduct}
                // Este sheet solo se abre desde el flow multi-modelo
                // (cuando el user toca "Utilizando..." en un slot del
                // ModelPicker en design-modelo), así que la copy va
                // alineada con ese modo.
                presetKind="design-modelo"
              />
            </div>
          )}
          {view === 'pick' && (
            <div className="flex-1 min-h-0 relative">
              {/* Desktop (modo directo): X para cerrar, arriba a la derecha,
                  sobre el header del panel. */}
              {useDesktopPick && (
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute top-4 right-4 z-10 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <PickProductPanel
                variant={useDesktopPick ? 'desktop' : 'mobile'}
                onConfirm={(products) => onConfirm(products)}
                // En modo directo "Volver/Cancelar" cierra el sheet; sino
                // vuelve a la pantalla Choose.
                onBack={directToPick ? onClose : () => setView('choose')}
                tiposProductoSugeridos={tiposProductoSugeridos ?? undefined}
                rubro={rubro}
                initialSelectedIds={initialSelection?.map((p) => p.id)}
                showPrimary={showPrimary}
                hideBack={useDesktopPick && directToPick}
                title={title}
                description={pickDescription}
              />
            </div>
          )}
          {view === 'upload' && (
            // variant='desktop' renderiza header / scroll / footer
            // fijos con `px-6` interno — el patrón correcto para side
            // panel y también se ve bien en mobile bottom sheet. El
            // variant='mobile' sería un flex-col sin padding, que
            // extiende los thumbs hasta los bordes del sheet y mata
            // visualmente las esquinas redondeadas.
            <UploadProductPanel
              variant="desktop"
              files={pendingFiles}
              name={pendingName}
              onNameChange={setPendingName}
              onAddMore={handleAddMoreFiles}
              onRemoveFile={handleRemoveFile}
              onCancel={handleCancelUpload}
              onConfirm={handleConfirmUpload}
              uploading={uploading}
              error={uploadError}
              rubro={rubro}
              onBulkUpload={() => setBulkOpen(true)}
            />
          )}
        </div>
      </div>
      {/* zIndex 220 > el z-211 de este sheet: si no, el popup queda abajo. */}
      <AgregarVariosModal
        open={bulkOpen}
        companyId={userCompanyId}
        brandId={brandId}
        userPlan={userPlan ?? ''}
        zIndex={220}
        onClose={() => setBulkOpen(false)}
        onSaved={handleBulkSaved}
      />
    </>,
    document.body,
  );
};
